import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { authApi, inr, fmtDate } from '@/lib/erpApi';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, FileSignature } from 'lucide-react';

const STATUSES = ['Active', 'Completed', 'Cancelled'];

const blank = {
  customer_id: '', project_name: '', plot_number: '', plot_size_sqft: '', plot_facing: '',
  total_value: 0, booking_amount: 0, booking_date: '',
  emi_amount: 0, emi_count: 12, emi_start_date: '', emi_due_day: 5,
  interest_rate: 0, status: 'Active', notes: '',
};

export default function EmiPlansPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/erp/emi/plans'),
        api.get('/erp/emi/customers'),
      ]);
      setRows(p.data); setCustomers(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const customerMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);

  const openNew = () => {
    if (!customers.length) return toast.error('Create an EMI customer first');
    setEditing(null); setForm({ ...blank, customer_id: customers[0].id }); setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...blank, ...p, plot_size_sqft: p.plot_size_sqft ?? '' });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.plot_number || !form.booking_date || !form.emi_start_date)
      return toast.error('Customer, plot number, booking date & EMI start required');
    setSaving(true);
    try {
      const payload = { ...form };
      ['total_value', 'booking_amount', 'emi_amount', 'emi_count', 'emi_due_day', 'interest_rate']
        .forEach((k) => { payload[k] = Number(payload[k] || 0); });
      payload.plot_size_sqft = payload.plot_size_sqft === '' ? null : Number(payload.plot_size_sqft);
      if (editing) await api.put(`/erp/emi/plans/${editing.id}`, payload);
      else await api.post('/erp/emi/plans', payload);
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete plan ${p.plot_number}?`)) return;
    try {
      await api.delete(`/erp/emi/plans/${p.id}`);
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="erp-emi-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">EMI Plans</h1>
          <p className="text-gray-500 text-sm mt-1">Plot bookings + EMI schedule. Collection UI activates in Phase 2.</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-emi-plan-btn">
          <Plus className="h-4 w-4 mr-2" /> New EMI Plan
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No EMI plans yet.</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="p-3">Customer</th>
                <th className="p-3">Project · Plot</th>
                <th className="p-3">Total</th>
                <th className="p-3">EMI</th>
                <th className="p-3">Booking</th>
                <th className="p-3">EMI Start</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-900 flex items-center gap-2"><FileSignature className="h-4 w-4 text-brand-orange" /> {customerMap[p.customer_id]?.name || '—'}</td>
                  <td className="p-3">{p.project_name} · <span className="font-mono">{p.plot_number}</span></td>
                  <td className="p-3 font-mono">{inr(p.total_value)}</td>
                  <td className="p-3 font-mono">{inr(p.emi_amount)} × {p.emi_count}</td>
                  <td className="p-3 text-gray-600 text-xs">{fmtDate(p.booking_date)}</td>
                  <td className="p-3 text-gray-600 text-xs">{fmtDate(p.emi_start_date)}</td>
                  <td className="p-3"><Badge variant={p.status === 'Active' ? 'default' : 'outline'} className={p.status === 'Active' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : ''}>{p.status}</Badge></td>
                  <td className="p-3 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(p)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit EMI Plan' : 'New EMI Plan'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="space-y-1 md:col-span-3">
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 md:col-span-2"><Label>Project Name *</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Plot Number *</Label><Input value={form.plot_number} onChange={(e) => setForm({ ...form, plot_number: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Plot Size (sqft)</Label><Input type="number" value={form.plot_size_sqft} onChange={(e) => setForm({ ...form, plot_size_sqft: e.target.value })} /></div>
              <div className="space-y-1"><Label>Facing</Label><Input value={form.plot_facing || ''} onChange={(e) => setForm({ ...form, plot_facing: e.target.value })} /></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Total Value (₹) *</Label><Input type="number" value={form.total_value} onChange={(e) => setForm({ ...form, total_value: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Booking Amount (₹)</Label><Input type="number" value={form.booking_amount} onChange={(e) => setForm({ ...form, booking_amount: e.target.value })} /></div>
              <div className="space-y-1"><Label>Booking Date *</Label><Input type="date" value={form.booking_date} onChange={(e) => setForm({ ...form, booking_date: e.target.value })} required /></div>
              <div className="space-y-1"><Label>EMI Amount (₹) *</Label><Input type="number" value={form.emi_amount} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} required /></div>
              <div className="space-y-1"><Label>EMI Count *</Label><Input type="number" min={1} value={form.emi_count} onChange={(e) => setForm({ ...form, emi_count: e.target.value })} required /></div>
              <div className="space-y-1"><Label>EMI Start Date *</Label><Input type="date" value={form.emi_start_date} onChange={(e) => setForm({ ...form, emi_start_date: e.target.value })} required /></div>
              <div className="space-y-1"><Label>Due Day (1–31)</Label><Input type="number" min={1} max={31} value={form.emi_due_day} onChange={(e) => setForm({ ...form, emi_due_day: e.target.value })} /></div>
              <div className="space-y-1"><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} /></div>
              <div className="space-y-1 md:col-span-3"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
